<script setup lang="ts">
import { z } from "zod";
import { maskito as vMaskito } from "@maskito/vue";
import type { MaskitoOptions } from "@maskito/core";
import { licenseNumberRegex } from "@carnet/api/auth";
import { initResetPassword } from "~/services/auth";
import type { FormSubmitEvent } from "#ui/types";

definePageMeta({
  layout: "auth",
  name: "password.reset",
  middleware: ["unauthenticated"],
});

const { t } = useI18n();
const localePath = useLocalePath();

useSeoMeta({ title: () => t("signin.password.reset.title") });

const route = useRoute();
const router = useRouter();

const state = reactive({
  identifier: "",
});

const schema = z.object({
  identifier: z.string().min(1, t("form.validation.identifier_required")).regex(licenseNumberRegex, t("form.validation.identifier_format")),
});

type Schema = z.output<typeof schema>;

const isLoading = ref<boolean>(false);

const emailHasBeenSent = ref<boolean>(false);

const error = ref<{ message: string } | null>(null);

watch(
  () => route.name,
  (name, oldName) => {
    if (name !== oldName) emailHasBeenSent.value = false;
  }
);

const onSubmit = (event: FormSubmitEvent<Schema>): void => {
  isLoading.value = true;

  initResetPassword(state.identifier).then(() => {
    emailHasBeenSent.value = true;
    isLoading.value = false;
  });
};

const handleAlertConfirm = () => {
  router.push(localePath({ name: "login" }));
};

const identifierMaskOptions: MaskitoOptions = {
  mask: [...Array(1).fill(/\w/), "-", ...Array(2).fill(/\w/), "-", ...Array(7).fill(/\w/)],
  preprocessors: [
    ({ elementState, data }) => {
      const { value, selection } = elementState;
      return {
        elementState: {
          selection,
          value: value.toUpperCase().replace(" ", "-"),
        },
        data: data.toUpperCase().replace(" ", "-"),
      };
    },
  ],
};
</script>

<template>
  <AppAuthFormLayout :title="$t('signin.password.reset.title')" :description="$t('signin.password.reset.description')">
    <AppForm id="password-reset-form" :schema :state class="not-last:mb-8 flex flex-col w-full" @submit="onSubmit">
      <div class="max-sm:grow not-last:mb-8">
        <UFormField name="identifier" :label="$t('login.identifier.label')" class="not-last:mb-8">
          <template #hint>
            <AppInputIdentifierHelper />
          </template>
          <UInput
            v-model="state.identifier"
            variant="soft"
            :placeholder="$t('register.identifier.placeholder')"
            v-maskito="identifierMaskOptions"
          />
        </UFormField>
        <div v-if="error">
          <Transition name="message-fade">
            <AppMessageWrapper>
              {{ error.message }}
            </AppMessageWrapper>
          </Transition>
        </div>
      </div>
    </AppForm>
    <template #actions>
      <AppLoadingButton hide-text-on-load type="submit" form="password-reset-form" block color="secondary" :loading="isLoading">
        {{ $t("ui.continue") }}
      </AppLoadingButton>
    </template>
  </AppAuthFormLayout>

  <AppDialog
    v-model:open="emailHasBeenSent"
    :title="$t('signin.password.reset.alert.header')"
    :buttons="[{ label: $t('login.submit'), onClick: handleAlertConfirm }]"
  >
    <template #description>
      <span>{{ $t("signin.password.reset.alert.subheader") }}</span>
    </template>
    <template #body>
      <p class="text-center">{{ $t("signin.password.reset.alert.message") }}</p>
    </template>
  </AppDialog>
</template>
